import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';

import { boardSearchSchema, type BoardSearch } from '@/features/board/search';
import { myTasksSearchSchema, type MyTasksSearch } from '@/pages/MyTasksPage/search';
import { ForgotPage } from '@/pages/ForgotPage/ForgotPage';
import { LoginPage } from '@/pages/LoginPage/LoginPage';
import { ResetPage } from '@/pages/ResetPage/ResetPage';
import { RegisterPage } from '@/pages/RegisterPage/RegisterPage';
import { NotFoundPage } from '@/pages/NotFoundPage/NotFoundPage';
import type { SpaceTab } from '@/pages/SpacePage/SpacePage';
import { StatusPage } from '@/pages/StatusPage/StatusPage';
import { getSession, refreshSession } from '@/shared/auth';

import { AppShell } from './layout/AppShell';
import { PublicLayout } from './layout/PublicLayout';
import { WorkspaceLayout } from './layout/WorkspaceLayout';

// Экраны приложения грузятся по требованию: каждый — свой кусок сборки,
// общее (редактор, dnd, таблица) Rollup выносит в разделяемые куски. Без
// этого вход и первый экран ждали, пока телефон разберёт два мегабайта
// скрипта, большая часть которого нужна только доске. Открытая часть
// (вход, регистрация, сброс) остаётся в главном куске — она и так мала.
const rootRoute = createRootRoute({
  notFoundComponent: NotFoundPage,
});

/**
 * Открытая часть: вход (и приём приглашения — в 0.7).
 *
 * Без оболочки приложения — у человека ещё нет ни разделов, ни учётной
 * записи, и показывать ему боковое меню, по которому нельзя ходить, незачем.
 */
const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  component: PublicLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/login',
  component: LoginPage,
  // Куда вернуть после входа. Разбор, а не доверие: в адресной строке может
  // оказаться что угодно, включая ссылку на чужой сайт.
  //
  // Возвращается либо поле, либо пустой объект, а не поле со значением
  // undefined: иначе роутер считает параметр обязательным, и каждый переход
  // на вход пришлось бы сопровождать пустым search.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search['redirect'] === 'string' ? { redirect: search['redirect'] } : {},
});

/**
 * Защищённая часть.
 *
 * Проверка стоит на общем родителе, а не в каждом экране: экран, забывший
 * проверить вход, выглядит работающим ровно до первого запроса — и это
 * худший способ узнать об ошибке.
 */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: AppShell,
  beforeLoad: async ({ location }) => {
    // Токен доступа живёт в памяти вкладки и умирает вместе с ней, поэтому
    // при первой загрузке сеанс ещё неизвестен: его восстанавливают из
    // cookie. Ждём здесь, иначе перезагрузка страницы выкидывала бы на вход
    // человека, который из системы не выходил.
    if (getSession().status === 'unknown') {
      await refreshSession();
    }

    if (getSession().status !== 'authenticated') {
      // Именно throw: так устроен переход в роутере — он ловит это значение
      // и уводит на другой маршрут. Значение не ошибка, поэтому правило про
      // выброс исключений здесь неприменимо.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
});

const homeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: lazyRouteComponent(() => import('@/pages/HomePage/HomePage'), 'HomePage'),
});

const profileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/profile',
  component: lazyRouteComponent(() => import('@/pages/ProfilePage/ProfilePage'), 'ProfilePage'),
});

const registerRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/register',
  component: RegisterPage,
});

const inviteRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/invite/$token',
  component: lazyRouteComponent(() => import('@/pages/InvitePage/InvitePage'), 'InvitePage'),
});

const forgotRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/forgot',
  component: ForgotPage,
});

const resetRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/reset/$token',
  component: ResetPage,
});

// Всё внутри пространства — под /app/w/{slug}. Префикс w нужен, чтобы slug
// не спорил с глобальными разделами вроде /app/profile.
const workspaceRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/w/$slug',
  component: WorkspaceLayout,
});

const workspaceHomeRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('@/pages/WorkspacePage/WorkspacePage'),
    'WorkspacePage',
  ),
});

const membersRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/members',
  component: lazyRouteComponent(() => import('@/pages/MembersPage/MembersPage'), 'MembersPage'),
});

// Проект: /app/w/{slug}/s/{space}. Вкладка живёт в ?tab=, чтобы ссылку на
// участников или настройки можно было переслать.
const spaceRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/s/$space',
  component: lazyRouteComponent(() => import('@/pages/SpacePage/SpacePage'), 'SpacePage'),
  validateSearch: (search: Record<string, unknown>): { tab?: SpaceTab } =>
    search['tab'] === 'members' ||
    search['tab'] === 'settings' ||
    search['tab'] === 'pages' ||
    search['tab'] === 'sprints'
      ? { tab: search['tab'] }
      : {},
});

/**
 * Корень домена зарезервирован под лендинг (решение Давида, 12 сентября
 * 2026): продукт живёт под /app, а / когда-нибудь станет открытой
 * страницей. До тех пор корень просто уводит в приложение.
 */
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/app' });
  },
});

// Служебные экраны — только в dev-сборке: в проде таких маршрутов нет
// вовсе, и tree-shaking выкидывает сами страницы вместе с ними.
const uiRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/ui',
  component: lazyRouteComponent(() => import('@/pages/UiPage/UiPage'), 'UiPage'),
});

const statusRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/status',
  component: StatusPage,
});

// Открытая задача живёт в ?task=TSK-N: ссылка на задачу — это ссылка на
// доску с открытой панелью, и переслать её можно как есть. Рядом — фильтры,
// сортировка и поиск (features/board/search.ts): пересылаемая ссылка несёт
// и их.
const boardRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/s/$space/b/$board',
  component: lazyRouteComponent(() => import('@/pages/BoardPage/BoardPage'), 'BoardPage'),
  validateSearch: (search: Record<string, unknown>): BoardSearch => boardSearchSchema.parse(search),
});

// Мои задачи: сквозной список поверх проектов (roadmap 8.0). Роль,
// завершённые и группировка живут в адресе — ссылку можно сохранить.
const myTasksRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/my',
  component: lazyRouteComponent(() => import('@/pages/MyTasksPage/MyTasksPage'), 'MyTasksPage'),
  validateSearch: (search: Record<string, unknown>): MyTasksSearch =>
    myTasksSearchSchema.parse(search),
});

const inboxRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/inbox',
  component: lazyRouteComponent(() => import('@/pages/InboxPage/InboxPage'), 'InboxPage'),
});

// Страницы: организации — /app/w/{slug}/p/{id}, проекта —
// /app/w/{slug}/s/{space}/p/{id}. Один экран, уровень из адреса.
const workspacePageRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/p/$pageId',
  component: lazyRouteComponent(() => import('@/pages/PagePage/PagePage'), 'PagePage'),
});

const spacePageRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/s/$space/p/$pageId',
  component: lazyRouteComponent(() => import('@/pages/PagePage/PagePage'), 'PagePage'),
});

// Задача страницей: /app/w/{slug}/t/TSK-N. Проекта и доски в адресе нет —
// такую ссылку удобно кидать в чат, а страница узнаёт их у сервера сама.
// Это третий режим показа задачи рядом с панелью и окном на доске.
const taskRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/t/$key',
  component: lazyRouteComponent(() => import('@/pages/TaskPage/TaskPage'), 'TaskPage'),
});

// Дерево маршрутов вынесено отдельно от роутера: тесты собирают свой
// экземпляр с историей в памяти, а браузерный роутер один на приложение.
export const routeTree = rootRoute.addChildren([
  landingRoute,
  publicRoute.addChildren(
    import.meta.env.DEV
      ? [loginRoute, registerRoute, inviteRoute, forgotRoute, resetRoute, uiRoute, statusRoute]
      : [loginRoute, registerRoute, inviteRoute, forgotRoute, resetRoute],
  ),
  authedRoute.addChildren([
    homeRoute,
    profileRoute,
    workspaceRoute.addChildren([
      workspaceHomeRoute,
      membersRoute,
      spaceRoute,
      boardRoute,
      taskRoute,
      workspacePageRoute,
      spacePageRoute,
      myTasksRoute,
      inboxRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  // Пока кусок экрана едет по сети (первый визит, после — из кэша
  // service worker), показывается пустое место, а не белый экран:
  // оболочка вокруг уже на месте. Пауза в полсекунды — чтобы при
  // мгновенной загрузке ничего не мигало.
  defaultPendingMs: 500,
  defaultPendingMinMs: 200,
});

// Регистрация типа роутера — именно она делает пути и параметры
// типизированными во всём приложении: опечатка в адресе перестаёт быть
// ошибкой рантайма и становится ошибкой сборки.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
